import "../sprites";
import { getSprite, spriteKeys, type SpriteSource } from "../sprites";

interface AtlasUV {
  u: number;
  v: number;
  w: number;
  h: number;
  pxW: number;
  pxH: number;
}

export interface SpriteAtlas {
  texture: GPUTexture;
  view: GPUTextureView;
  sampler: GPUSampler;
  width: number;
  height: number;
  lookup: Map<string, AtlasUV>;
}

const RASTER_SCALE = 4;
const ATLAS_W = 1024;
const ATLAS_H = 1024;

export async function buildSpriteAtlas(device: GPUDevice): Promise<SpriteAtlas> {
  const keys = spriteKeys();
  if (keys.length === 0) {
    throw new Error("sprite registry is empty — did you import src/sprites?");
  }

  const rasters: { key: string; bmp: ImageBitmap; src: SpriteSource }[] = [];
  await Promise.all(
    keys.map(async (key) => {
      const src = getSprite(key);
      const bmp = await rasterizeSVG(src, RASTER_SCALE);
      rasters.push({ key, bmp, src });
    }),
  );

  rasters.sort((a, b) => b.bmp.height - a.bmp.height);

  const lookup = new Map<string, AtlasUV>();
  const canvas = new OffscreenCanvas(ATLAS_W, ATLAS_H);
  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) throw new Error("could not get OffscreenCanvas 2d context");
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, ATLAS_W, ATLAS_H);

  // 1px gutter prevents bilinear bleed.
  const GUTTER = 1;
  let cursorX = GUTTER;
  let cursorY = GUTTER;
  let rowHeight = 0;

  for (const r of rasters) {
    const w = r.bmp.width;
    const h = r.bmp.height;
    if (cursorX + w + GUTTER > ATLAS_W) {
      cursorX = GUTTER;
      cursorY += rowHeight + GUTTER;
      rowHeight = 0;
    }
    if (cursorY + h + GUTTER > ATLAS_H) {
      throw new Error("sprite atlas overflow — raise ATLAS dims or shrink RASTER_SCALE");
    }
    ctx.drawImage(r.bmp, cursorX, cursorY);
    lookup.set(r.key, {
      u: cursorX / ATLAS_W,
      v: cursorY / ATLAS_H,
      w: w / ATLAS_W,
      h: h / ATLAS_H,
      pxW: r.src.width,
      pxH: r.src.height,
    });
    cursorX += w + GUTTER;
    if (h > rowHeight) rowHeight = h;
  }

  const texture = device.createTexture({
    label: "sprite-atlas",
    size: { width: ATLAS_W, height: ATLAS_H, depthOrArrayLayers: 1 },
    format: "rgba8unorm",
    usage:
      GPUTextureUsage.TEXTURE_BINDING |
      GPUTextureUsage.COPY_DST |
      GPUTextureUsage.RENDER_ATTACHMENT,
  });
  device.queue.copyExternalImageToTexture(
    { source: canvas, flipY: false },
    { texture },
    { width: ATLAS_W, height: ATLAS_H, depthOrArrayLayers: 1 },
  );

  const sampler = device.createSampler({
    magFilter: "linear",
    minFilter: "linear",
    addressModeU: "clamp-to-edge",
    addressModeV: "clamp-to-edge",
  });

  return {
    texture,
    view: texture.createView(),
    sampler,
    width: ATLAS_W,
    height: ATLAS_H,
    lookup,
  };
}

async function rasterizeSVG(src: SpriteSource, scale: number): Promise<ImageBitmap> {
  const w = src.width * scale;
  const h = src.height * scale;
  const blob = new Blob([src.svg], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  try {
    const bmp = await createImageBitmap(blob, {
      resizeWidth: w,
      resizeHeight: h,
      resizeQuality: "pixelated",
    });
    return bmp;
  } catch {
    // Older Safari can't decode SVG blobs via createImageBitmap directly.
    const img = new Image();
    img.src = url;
    await img.decode();
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no 2d context for SVG fallback");
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.transferToImageBitmap();
  } finally {
    URL.revokeObjectURL(url);
  }
}
